/**
 * Settings → Model portal: wie van de modellen kan inloggen op /portal.
 *
 * Fase 2 (go Jordy 02-10-2026). Inzien mag beheer; aanmaken, uitnodigen,
 * blokkeren, intrekken en 2FA resetten alleen de eigenaar (de server dwingt dat
 * af). Er gaat geen mail uit: de uitnodigingslink staat hier één keer in beeld
 * en de eigenaar stuurt hem zelf door (keuze Jordy 02-10-2026).
 */
import { useState } from 'react'
import { ErrorNote, Loading, Panel, Table, Tag } from './ui'
import { Modal, Field } from './Modal'
import { ModelKeuze, type ModelOptie } from './ModelKeuze'
import { useApi } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { useMij } from '../lib/rechten'
import { api } from '../lib/api'
import { dt } from '../lib/format'

interface PortalRegel {
  id: number
  name: string
  persona_ids: number[]
  account_id: number | null
  email: string | null
  status: string | null
  last_login: string | null
  two_factor_enabled: boolean
}

const STATUS_KLEUR: Record<string, 'ok' | 'warn' | 'bad'> = { active: 'ok', invited: 'warn', blocked: 'bad', revoked: 'bad' }

function LinkTonen({ link, onClose }: { link: string; onClose: () => void }) {
  const [gekopieerd, setGekopieerd] = useState(false)
  return (
    <Modal title="Invitation link" onClose={onClose}
           desc="Send this link to the model yourself. It works once and expires after 7 days. It is shown only now.">
      <div className="mono" style={{ wordBreak: 'break-all', fontSize: 12.5, marginBottom: 10 }}>{link}</div>
      <button className="btn primary sm" type="button"
              onClick={() => navigator.clipboard.writeText(link).then(() => setGekopieerd(true))}>
        {gekopieerd ? 'Copied' : 'Copy link'}
      </button>
    </Modal>
  )
}

export function ModelPortalBeheer() {
  const mij = useMij()
  const eigenaar = String(mij.data?.role ?? '').toLowerCase() === 'owner'
  const rijen = useApi<{ items: PortalRegel[] }>('/api/admin/model-portal')
  const earnings = useApi<{ enabled: boolean }>('/api/admin/model-portal/earnings-switch')
  const modellen = useApi<ModelOptie[]>('/api/admin/models/personas?active_only=true&linked_only=true')
  const { run, busy } = useActions()
  const [nieuw, setNieuw] = useState<{ name: string; email: string; ids: number[] } | null>(null)
  const [profielen, setProfielen] = useState<{ id: number; name: string; ids: number[] } | null>(null)
  const [link, setLink] = useState<string | null>(null)
  const [fout, setFout] = useState('')

  const naamVan = (id: number) => {
    const m = (modellen.data ?? []).find((x) => x.id === id)
    // De koppeltabel (§2) moet zichtbaar maken wélk platformrecord het is.
    const plat = m && (m.f2f_model_id || m.slug.startsWith('f2f_')) ? 'F2F' : 'FC'
    return m ? `${m.stage_name || m.slug} (${plat})` : `#${id}`
  }

  async function aanmaken() {
    if (!nieuw) return
    setFout('')
    try {
      const r = await api.post<{ invite_link: string }>('/api/admin/model-portal',
        { name: nieuw.name.trim(), email: nieuw.email.trim(), persona_ids: nieuw.ids })
      setNieuw(null); setLink(r.invite_link); rijen.refetch()
    } catch (e) { setFout(e instanceof Error ? e.message : String(e)) }
  }

  async function uitnodigen(r: PortalRegel) {
    const ok = window.confirm(`Create a new invitation link for ${r.name}? Earlier unused links stop working.`)
    if (!ok) return
    try {
      const uit = await api.post<{ invite_link: string }>(`/api/admin/model-portal/${r.id}/invite`)
      setLink(uit.invite_link); rijen.refetch()
    } catch (e) { setFout(e instanceof Error ? e.message : String(e)) }
  }

  const status = (r: PortalRegel, naar: string, vraag: string) => run({
    label: `portal-${r.id}-${naar}`, confirm: vraag,
    request: mut.post(`/api/admin/model-portal/${r.id}/status`, { status: naar }),
    onDone: rijen.refetch, successText: 'Saved',
  })

  return (
    <Panel title="Model portal" flush right={eigenaar ? (
      <button className="btn primary sm" type="button" onClick={() => { setFout(''); setNieuw({ name: '', email: '', ids: [] }) }}>
        + Add model login
      </button>
    ) : null}>
      <div className="mut2" style={{ fontSize: 12, padding: '0 14px 10px' }}>
        Models sign in at <span className="mono">/portal</span> and see only their own sales and share.
        {eigenaar ? '' : ' Only the owner can make changes.'}
      </div>
      {/* 04-10-2026 (Jordy): de Earnings-pagina van het portal gaat pas open na zijn akkoord. */}
      <div className="row" style={{ gap: 8, alignItems: 'center', padding: '0 14px 10px', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 12.5 }}>
          Earnings in the portal: <b>{earnings.data == null ? '…' : earnings.data.enabled ? 'visible to models' : 'hidden'}</b>
        </span>
        {eigenaar && earnings.data ? (
          <button className="btn ghost sm" type="button" disabled={!!busy}
                  onClick={() => run({
                    label: 'portal-earnings',
                    confirm: earnings.data!.enabled
                      ? 'Hide the earnings page from all models?'
                      : 'Show the earnings page (sales and their share) to all models with a login?',
                    request: mut.post('/api/admin/model-portal/earnings-switch', { enabled: !earnings.data!.enabled }),
                    onDone: earnings.refetch, successText: 'Saved',
                  })}>
            {earnings.data.enabled ? 'Hide' : 'Show'}
          </button>
        ) : null}
      </div>
      {fout ? <div style={{ padding: '0 14px 10px' }}><ErrorNote error={fout} /></div> : null}
      {rijen.loading ? <Loading /> : rijen.error ? <ErrorNote error={rijen.error} /> : (
        <Table cols={[
          { key: 'n', label: 'Model' }, { key: 'e', label: 'Email' }, { key: 'p', label: 'Profiles' },
          { key: 's', label: 'Status' }, { key: 'l', label: 'Last login' }, { key: 'f', label: '2FA' },
          { key: 'a', label: '' },
        ]} emptyText="No model logins yet.">
          {(rijen.data?.items ?? []).map((r) => (
            <tr key={r.id}>
              <td><b>{r.name}</b></td>
              <td className="mono">{r.email ?? '—'}</td>
              <td>{r.persona_ids.map(naamVan).join(', ') || '—'}</td>
              <td>{r.status ? <Tag kind={STATUS_KLEUR[r.status] ?? 'mut'}>{r.status}</Tag> : '—'}</td>
              <td>{r.last_login ? dt(r.last_login) : '—'}</td>
              <td>{r.two_factor_enabled ? 'On' : 'Off'}</td>
              <td>
                {eigenaar && r.status !== 'revoked' ? (
                  <div className="row" style={{ gap: 4, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                    <button className="btn ghost sm" type="button"
                            onClick={() => setProfielen({ id: r.id, name: r.name, ids: r.persona_ids })}>Profiles</button>
                    <button className="btn ghost sm" type="button" onClick={() => uitnodigen(r)}>New link</button>
                    {r.status === 'blocked' ? (
                      <button className="btn ghost sm" type="button" disabled={!!busy}
                              onClick={() => status(r, 'active', `Unblock ${r.name}?`)}>Unblock</button>
                    ) : (
                      <button className="btn ghost sm" type="button" disabled={!!busy}
                              onClick={() => status(r, 'blocked', `Block ${r.name}? They are signed out at once.`)}>Block</button>
                    )}
                    {r.two_factor_enabled ? (
                      <button className="btn ghost sm" type="button" disabled={!!busy}
                              onClick={() => run({
                                label: `portal-${r.id}-2fa`, confirm: `Turn off 2FA for ${r.name}? They are signed out at once.`,
                                request: mut.post(`/api/admin/model-portal/${r.id}/2fa-reset`),
                                onDone: rijen.refetch, successText: '2FA reset',
                              })}>Reset 2FA</button>
                    ) : null}
                    <button className="btn ghost sm" type="button" disabled={!!busy}
                            onClick={() => status(r, 'revoked',
                              `Revoke access for ${r.name}? The login stops working for good; a new login needs a new invitation.`)}>
                      Revoke
                    </button>
                  </div>
                ) : null}
              </td>
            </tr>
          ))}
        </Table>
      )}

      {nieuw ? (
        <Modal title="Add model login" width={520} onClose={() => setNieuw(null)}
               desc="Creates the login and an invitation link. No email is sent."
               footer={<>
                 <button className="btn ghost sm" type="button" onClick={() => setNieuw(null)}>Cancel</button>
                 <button className="btn primary sm" type="button" onClick={aanmaken}
                         disabled={!nieuw.name.trim() || !nieuw.email.trim() || nieuw.ids.length === 0}>
                   Create and get link
                 </button>
               </>}>
          <Field label="Name"><input className="inp" value={nieuw.name}
                 onChange={(e) => setNieuw({ ...nieuw, name: e.target.value })} /></Field>
          <Field label="Email (used to sign in)"><input className="inp" type="email" value={nieuw.email}
                 onChange={(e) => setNieuw({ ...nieuw, email: e.target.value })} /></Field>
          <Field label="Platform profiles of this model">
            {modellen.loading ? <Loading /> : (
              <ModelKeuze modellen={modellen.data ?? []} gekozen={nieuw.ids}
                          onChange={(ids) => setNieuw({ ...nieuw, ids })} />
            )}
          </Field>
          {fout ? <ErrorNote error={fout} /> : null}
        </Modal>
      ) : null}

      {profielen ? (
        <Modal title={`Profiles — ${profielen.name}`} width={520} onClose={() => setProfielen(null)}
               footer={<>
                 <button className="btn ghost sm" type="button" onClick={() => setProfielen(null)}>Cancel</button>
                 <button className="btn primary sm" type="button" disabled={profielen.ids.length === 0 || !!busy}
                         onClick={() => run({
                           label: `portal-${profielen.id}-profielen`,
                           request: mut.put(`/api/admin/model-portal/${profielen.id}/profiles`, { persona_ids: profielen.ids }),
                           onDone: () => { setProfielen(null); rijen.refetch() }, successText: 'Profiles saved',
                         })}>Save</button>
               </>}>
          <ModelKeuze modellen={modellen.data ?? []} gekozen={profielen.ids}
                      onChange={(ids) => setProfielen({ ...profielen, ids })} />
        </Modal>
      ) : null}

      {link ? <LinkTonen link={link} onClose={() => setLink(null)} /> : null}
    </Panel>
  )
}
